import { useState, useRef, useCallback, useEffect } from 'react';

// Minimum blob size in bytes to consider audio as potentially containing speech.
// A valid WebM header alone can be several hundred bytes with no real audio.
const MIN_AUDIO_BLOB_SIZE = 5000;

// Shared audio constraints that request mic-only access without engaging
// the OS's "call-mode" audio session (which ducks / pauses other apps).
const AUDIO_CONSTRAINTS = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
};

export const useAudioRecorder = ({ onRecordingComplete } = {}) => {
  // Always start as 'prompt' — the user must tap once per session to confirm
  // mic access. This avoids stale permission states from
  // navigator.permissions.query or browser restarts that leave the button
  // looking ready to record when it isn't.
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [micPermission, setMicPermission] = useState('prompt');
  const [progress, setProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const isStoppingRef = useRef(false);
  const onRecordingCompleteRef = useRef(onRecordingComplete);

  // Keep the external callback handler up-to-date without causing re-renders
  useEffect(() => {
    onRecordingCompleteRef.current = onRecordingComplete;
  }, [onRecordingComplete]);

  // Validate browser support on mount (but do NOT check permission state —
  // we intentionally stay at 'prompt' until the user taps).
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Audio recording is not supported by your browser.');
      setMicPermission('denied');
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, []);

  /**
   * Create a fresh AudioContext and resume it. A NEW context is created each
   * recording because Chromium-based browsers only fully release the audio
   * duck when the context is CLOSED.
   */
  const createAudioContext = useCallback(async () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      await audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    const ctx = new AudioCtx();
    await ctx.resume();
    audioContextRef.current = ctx;
    return ctx;
  }, []);

  /**
   * Immediately release the mic stream and close the AudioContext.
   * Called synchronously at the START of stop so Chromium's audio-focus
   * un-duck timer begins as early as possible.
   */
  const releaseAudioResources = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const requestMicPermission = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errText = 'Audio recording is not supported by your browser.';
      setError(errText);
      setMicPermission('denied');
      return false;
    }
    try {
      // Pre-warm an AudioContext BEFORE requesting the mic so the OS sees a
      // "playback" session first, reducing the chance of ducking other apps.
      await createAudioContext();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS,
      });

      stream.getTracks().forEach((track) => track.stop());
      releaseAudioResources();

      // This is the ONLY path that sets permission to 'granted'.
      setMicPermission('granted');
      return true;
    } catch (err) {
      console.error('Error requesting microphone permission:', err);
      releaseAudioResources();
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError(
          'Microphone access was denied. Please allow microphone access in your browser settings.'
        );
        setMicPermission('denied');
      } else {
        setError('Could not access microphone.');
      }
      return false;
    }
  }, [createAudioContext, releaseAudioResources]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (isStoppingRef.current || !mediaRecorderRef.current) {
        resolve({ audioBlob: null, duration: 0 });
        return;
      }
      isStoppingRef.current = true;

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      const duration = startTimeRef.current ? Date.now() - startTimeRef.current : 0;

      // Release mic tracks and AudioContext immediately so Chromium's
      // un-duck timer starts as early as possible.
      releaseAudioResources();

      mediaRecorderRef.current.onstop = () => {
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;

        setIsRecording(false);
        setElapsedTime(0);
        setProgress(0);
        setRemainingTime(0);

        if (audioBlob.size < MIN_AUDIO_BLOB_SIZE) {
          console.warn(
            `Audio blob too small (${audioBlob.size} bytes). Discarding to prevent hallucination.`
          );
          isStoppingRef.current = false;
          resolve({ audioBlob: null, duration: 0 });
          return;
        }

        if (onRecordingCompleteRef.current) {
          onRecordingCompleteRef.current({ audioBlob, duration });
        }

        isStoppingRef.current = false;
        resolve({ audioBlob, duration });
      };

      mediaRecorderRef.current.onerror = (event) => {
        setError('An error occurred during recording.');
        setIsRecording(false);
        mediaRecorderRef.current = null;
        isStoppingRef.current = false;
        reject(event.error);
      };

      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      } else {
        isStoppingRef.current = false;
        setIsRecording(false);
        mediaRecorderRef.current = null;
        resolve({ audioBlob: null, duration: 0 });
      }
    });
  }, [releaseAudioResources]);

  const startRecording = useCallback(
    async (timeLimit = 15000) => {
      if (isRecording) {
        console.warn('Recording is already in progress.');
        return;
      }
      setError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Audio recording is not supported by your browser.');
        return;
      }

      try {
        const audioCtx = await createAudioContext();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: AUDIO_CONSTRAINTS,
        });

        streamRef.current = stream;

        // If we got here, permission is definitely granted. This handles the
        // edge case where startRecording is called directly without going
        // through requestMicPermission first.
        if (micPermission !== 'granted') setMicPermission('granted');

        if (audioCtx) {
          const source = audioCtx.createMediaStreamSource(stream);
          void source;
        }

        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        audioChunksRef.current = [];
        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        recorder.start(250);
        startTimeRef.current = Date.now();
        setIsRecording(true);

        const intervalStartTime = Date.now();
        timerRef.current = setInterval(() => {
          const elapsed = Date.now() - intervalStartTime;
          setElapsedTime(elapsed);
          if (elapsed >= timeLimit) {
            stopRecording();
          } else {
            setProgress((elapsed / timeLimit) * 100);
            setRemainingTime(Math.ceil((timeLimit - elapsed) / 1000));
          }
        }, 50);
      } catch (err) {
        console.error('Error starting audio recording:', err);
        releaseAudioResources();
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError(
            'Microphone access was denied. Please allow microphone access in your browser settings.'
          );
          setMicPermission('denied');
        } else {
          setError('Could not start audio recording.');
        }
      }
    },
    [isRecording, micPermission, stopRecording, createAudioContext, releaseAudioResources]
  );

  return {
    isRecording,
    startRecording,
    stopRecording,
    error,
    micPermission,
    requestMicPermission,
    progress,
    remainingTime,
    elapsedTime,
  };
};